import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import "@/i18n";

import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { registerServiceWorker } from "@/lib/push";
import { isNative, registerNativePush, applyNativeStatusBar, hideNativeSplash } from "@/lib/native";

import Welcome from "@/pages/Welcome";
import Onboarding from "@/pages/Onboarding";
import Auth from "@/pages/Auth";
import Today from "@/pages/Today";
import Decode from "@/pages/Decode";
import Sanctuary from "@/pages/Sanctuary";
import Safety from "@/pages/Safety";
import Me from "@/pages/Me";
import Library from "@/pages/Library";
import LibraryDetail from "@/pages/LibraryDetail";
import BeforeYouGoBack from "@/pages/BeforeYouGoBack";
import EmergencyEscapeKit from "@/pages/EmergencyEscapeKit";
import LeavingSafelyChecklist from "@/pages/LeavingSafelyChecklist";
import Financial from "@/pages/Financial";
import FinancialDetail from "@/pages/FinancialDetail";
import IncidentLog from "@/pages/IncidentLog";
import Calculator from "@/pages/Calculator";
import Settings from "@/pages/Settings";
import SignalLetter from "@/pages/SignalLetter";
import Donate from "@/pages/Donate";
import SavedReflections from "@/pages/SavedReflections";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

// v1.1 — sanctuary sub-pages, lessons, path picker
import NervousSystem from "@/pages/sanctuary/NervousSystem";
import CycleAwareness from "@/pages/sanctuary/CycleAwareness";
import CommunityCircle from "@/pages/sanctuary/CommunityCircle";
import PetProtection from "@/pages/sanctuary/PetProtection";
import FinancialFreedomLesson from "@/pages/sanctuary/FinancialFreedom";
import ComfortCorner from "@/pages/sanctuary/ComfortCorner";
import Breath from "@/pages/sanctuary/Breath";
import { LgbtqHub, LgbtqArticle } from "@/pages/sanctuary/Lgbtq";
import Lessons from "@/pages/Lessons";
import LessonDetail from "@/pages/LessonDetail";
import PathPicker from "@/pages/PathPicker";

// Public marketing site (sistersignal.org)
import MarketingHome from "@/marketing/Home";
import { ResourcesHub, ResourceDetail } from "@/marketing/Resources";
import About from "@/marketing/About";
import SafetyPrivacy from "@/marketing/SafetyPrivacy";
import Press from "@/marketing/Press";
import Partners from "@/marketing/Partners";
import Volunteer from "@/marketing/Volunteer";
import DonateMarketing from "@/marketing/Donate";
import DonateSuccess from "@/marketing/DonateSuccess";
import WallPage from "@/marketing/Wall";
import MediaKit from "@/marketing/MediaKit";
import { PrivacyPolicy, Terms, Contact } from "@/marketing/Legal";
import AppEntry from "@/marketing/AppEntry";

function isOnboardedFlag() {
  try { return window.localStorage.getItem("ss_onboarded") === "1"; }
  catch { return false; }
}

function Gate({ children }) {
  const { profile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[100dvh] grid place-items-center bg-background text-foreground">
        <div className="font-serif italic text-2xl text-brand animate-soft-pulse">sister signal</div>
      </div>
    );
  }
  const onboarded = profile?.onboarded || isOnboardedFlag();
  if (!onboarded && location.pathname !== "/onboarding" && location.pathname !== "/welcome" && location.pathname !== "/auth" && location.pathname !== "/calc") {
    return <Navigate to="/welcome" replace />;
  }
  return children;
}

/**
 * In native (Capacitor / iOS) the frozen bundle deep-links straight into the
 * app (e.g. /today after onboarding). We do NOT want native users to see the
 * marketing landing — they tapped the app icon, not a marketing URL. So when
 * the app is running natively, "/" sends them to /welcome (existing behaviour).
 * Web visitors see the marketing home.
 */
function HomeRouter() {
  if (isNative()) return <Navigate to="/welcome" replace />;
  return <MarketingHome />;
}

function ThemedRoot() {
  const { theme } = useTheme();
  useEffect(() => {
    document.body.classList.toggle("night", theme === "night");
  }, [theme]);
  return (
    <>
      <Toaster
        position="top-center"
        theme={theme === "night" ? "dark" : "light"}
        toastOptions={{ className: "font-sans" }}
      />
      <Routes>
        {/* ── PUBLIC MARKETING SITE (sistersignal.org/*) ───────────────── */}
        <Route path="/"                element={<HomeRouter />} />
        <Route path="/resources"       element={<ResourcesHub />} />
        <Route path="/resources/:slug" element={<ResourceDetail />} />
        <Route path="/about"           element={<About />} />
        <Route path="/safety-privacy"  element={<SafetyPrivacy />} />
        <Route path="/press"           element={<Press />} />
        <Route path="/press/media-kit" element={<MediaKit />} />
        <Route path="/partners"        element={<Partners />} />
        <Route path="/volunteer"       element={<Volunteer />} />
        <Route path="/privacy"         element={<PrivacyPolicy />} />
        <Route path="/terms"           element={<Terms />} />
        <Route path="/contact"         element={<Contact />} />
        <Route path="/donate"          element={<DonateMarketing />} />
        <Route path="/donate/success"  element={<DonateSuccess />} />
        <Route path="/wall"            element={<WallPage />} />

        {/* ── WEB APP ENTRY (sistersignal.org/app) ─────────────────────── */}
        <Route path="/app" element={<AppEntry />} />

        {/* ── APP ROUTES (unchanged paths for iOS backward compat) ─────── */}
        <Route path="/calc" element={<Calculator />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset" element={<ResetPassword />} />

        <Route path="/today"     element={<Gate><Today /></Gate>} />
        <Route path="/decode"    element={<Gate><Decode /></Gate>} />
        <Route path="/sanctuary" element={<Gate><Sanctuary /></Gate>} />
        <Route path="/sanctuary/nervous-system" element={<Gate><NervousSystem /></Gate>} />
        <Route path="/sanctuary/cycle"          element={<Gate><CycleAwareness /></Gate>} />
        <Route path="/sanctuary/community"      element={<Gate><CommunityCircle /></Gate>} />
        <Route path="/sanctuary/pets"           element={<Gate><PetProtection /></Gate>} />
        <Route path="/sanctuary/financial"      element={<Gate><FinancialFreedomLesson /></Gate>} />
        <Route path="/sanctuary/comfort"        element={<Gate><ComfortCorner /></Gate>} />
        <Route path="/sanctuary/breath"         element={<Gate><Breath /></Gate>} />
        <Route path="/sanctuary/lgbtq"          element={<Gate><LgbtqHub /></Gate>} />
        <Route path="/sanctuary/lgbtq/:slug"    element={<Gate><LgbtqArticle /></Gate>} />
        <Route path="/lessons"          element={<Gate><Lessons /></Gate>} />
        <Route path="/lessons/:slug"    element={<Gate><LessonDetail /></Gate>} />
        <Route path="/path"             element={<Gate><PathPicker /></Gate>} />
        <Route path="/safety"    element={<Gate><Safety /></Gate>} />
        <Route path="/me"        element={<Gate><Me /></Gate>} />
        <Route path="/library"   element={<Gate><Library /></Gate>} />
        <Route path="/library/:slug" element={<Gate><LibraryDetail /></Gate>} />
        <Route path="/before-you-go-back" element={<Gate><BeforeYouGoBack /></Gate>} />
        <Route path="/emergency-escape-kit" element={<Gate><EmergencyEscapeKit /></Gate>} />
        <Route path="/leaving-safely-checklist" element={<Gate><LeavingSafelyChecklist /></Gate>} />
        <Route path="/reflections" element={<Gate><SavedReflections /></Gate>} />
        <Route path="/financial" element={<Gate><Financial /></Gate>} />
        <Route path="/financial/:slug" element={<Gate><FinancialDetail /></Gate>} />
        <Route path="/incident-log" element={<Gate><IncidentLog /></Gate>} />
        <Route path="/settings" element={<Gate><Settings /></Gate>} />
        <Route path="/signal-letter" element={<Gate><SignalLetter /></Gate>} />

        {/* ── CANONICAL /app/* MIRROR ROUTES (forward-looking) ─────────── */}
        {/* These render identical components. The marketing site links to */}
        {/* /app/* paths so the URL reads as "the app at /app/...". The    */}
        {/* legacy paths above stay alive so iOS WebView + existing        */}
        {/* bookmarks keep working without a forced redirect.              */}
        <Route path="/app/today"     element={<Gate><Today /></Gate>} />
        <Route path="/app/decode"    element={<Gate><Decode /></Gate>} />
        <Route path="/app/sanctuary" element={<Gate><Sanctuary /></Gate>} />
        <Route path="/app/safety"    element={<Gate><Safety /></Gate>} />
        <Route path="/app/me"        element={<Gate><Me /></Gate>} />
        <Route path="/app/library"   element={<Gate><Library /></Gate>} />
        <Route path="/app/library/:slug" element={<Gate><LibraryDetail /></Gate>} />
        <Route path="/app/financial" element={<Gate><Financial /></Gate>} />
        <Route path="/app/financial/:slug" element={<Gate><FinancialDetail /></Gate>} />
        <Route path="/app/incident-log" element={<Gate><IncidentLog /></Gate>} />
        <Route path="/app/before-you-go-back" element={<Gate><BeforeYouGoBack /></Gate>} />
        <Route path="/app/emergency-escape-kit" element={<Gate><EmergencyEscapeKit /></Gate>} />
        <Route path="/app/leaving-safely-checklist" element={<Gate><LeavingSafelyChecklist /></Gate>} />
        <Route path="/app/reflections" element={<Gate><SavedReflections /></Gate>} />
        <Route path="/app/lessons" element={<Gate><Lessons /></Gate>} />
        <Route path="/app/lessons/:slug" element={<Gate><LessonDetail /></Gate>} />
        <Route path="/app/settings" element={<Gate><Settings /></Gate>} />
        <Route path="/app/signal-letter" element={<Gate><SignalLetter /></Gate>} />
        <Route path="/app/donate" element={<Gate><Donate /></Gate>} />
        <Route path="/app/donate/success" element={<Gate><Donate /></Gate>} />
        <Route path="/app/welcome" element={<Welcome />} />
        <Route path="/app/onboarding" element={<Onboarding />} />
        <Route path="/app/auth" element={<Auth />} />

        {/* 404 — send web visitors to marketing home, native to /welcome */}
        <Route path="*" element={isNative() ? <Navigate to="/welcome" replace /> : <Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  useEffect(() => {
    if (isNative()) {
      hideNativeSplash();
      registerNativePush().catch(() => {});
    } else {
      registerServiceWorker();
    }
  }, []);
  return (
    <div className="App">
      <HelmetProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <ThemedRoot />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </HelmetProvider>
    </div>
  );
}
